import React, { useCallback, useEffect, useState } from 'react';
import { API_BASE_URL } from '../../utils/apiBase';
import { clientWhatsAppUrl } from '../../utils/studioWhatsApp';
import CorrespondenceTimeline, { TimelineEntry } from './CorrespondenceTimeline';
import { ShootMap } from '../Common/ShootLocationField';

export interface ClientBooking {
  dateConfirmed?: boolean;
  shootDate?: string;
  startTime?: string;
  endTime?: string;
  timezone?: string;
  locationName?: string;
  address?: string;
  mapsUrl?: string;
  calendarUrl?: string;
  clientNotes?: string;
  preparation?: Record<string, string> | null;
  completed?: boolean;
}

export interface ClientPortal {
  contact?: { hasEmail?: boolean; hasPhone?: boolean };
  booking?: ClientBooking | null;
  timeline?: TimelineEntry[];
}

const REFRESH_MS = 30000;

/** Loads the client's conversation and booking, and keeps it fresh while the page is open. */
export function useClientPortal(token?: string) {
  const [portal, setPortal] = useState<ClientPortal | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState('');

  const reload = useCallback(() => {
    if (!token) return Promise.resolve();
    return fetch(`${API_BASE_URL}/public/quotes/${token}/portal`)
      .then(async (response) => {
        if (!response.ok) throw new Error('Updates could not be loaded.');
        setPortal(await response.json());
        setLoadError('');
      })
      .catch(() => setLoadError('Updates could not be loaded.'))
      .then(() => setLoaded(true));
  }, [token]);

  useEffect(() => {
    reload();
    const timer = window.setInterval(() => {
      if (!document.hidden) reload();
    }, REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [reload]);

  const spoken = (portal?.timeline || []).filter((entry) => entry.kind === 'client-message' || entry.kind === 'studio-reply');
  const latest = spoken.slice().sort((a, b) => String(b.at || '').localeCompare(String(a.at || '')))[0];
  /** True when Carmen has the last word, so the client has something to read or answer. */
  const studioWaiting = Boolean(latest && latest.kind === 'studio-reply');

  return {
    portal,
    loaded,
    loadError,
    reload,
    spokenCount: spoken.length,
    studioWaiting,
    shootConfirmed: Boolean(portal?.booking?.dateConfirmed),
  };
}

export function formatShoot(date?: string, time?: string) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date || '');
  if (!match) return [date, time].filter(Boolean).join(' at ');
  const utc = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  const written = new Intl.DateTimeFormat('en-ZA', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(utc);
  return time ? `${written} at ${time}` : written;
}

/** The confirmed shoot: when, where, and anything to prepare. Shown inside the summary at the top. */
export const ClientShoot = ({ booking }: { booking: ClientBooking }) => {
  const preparationLines = booking.preparation
    ? Object.entries(booking.preparation).filter(([, value]) => value)
    : [];
  return (
    <div className="quote-booking-card" data-testid="confirmed-shoot">
      <span className="quote-booking-label">Booking</span>
      <p className="quote-shoot-when"><strong>{formatShoot(booking.shootDate, booking.startTime)}</strong>{booking.endTime ? ` until ${booking.endTime}` : ''}</p>
      {booking.timezone && booking.timezone !== 'Africa/Johannesburg' ? <p>{booking.timezone.replace(/_/g, ' ')}</p> : null}
      {booking.locationName ? <p>{booking.locationName}</p> : null}
      {booking.address ? <p>{booking.address}</p> : null}
      {!booking.locationName && !booking.address && !booking.mapsUrl ? <p>Location to be agreed. Carmen will confirm the place with you.</p> : null}
      {booking.mapsUrl ? <p><a href={booking.mapsUrl} target="_blank" rel="noreferrer">Open in Google Maps</a></p> : null}
      <ShootMap mapsUrl={booking.mapsUrl} label={booking.locationName || booking.address} />
      {booking.calendarUrl ? <p><a href={booking.calendarUrl} target="_blank" rel="noreferrer">Add to your calendar</a></p> : null}
      {booking.clientNotes ? <p>{booking.clientNotes}</p> : null}
      {preparationLines.map(([key, value]) => <p key={key}>{value}</p>)}
    </div>
  );
};

interface ChatProps {
  token: string;
  quoteNumber: string;
  portal: ClientPortal | null;
  studioWaiting: boolean;
  spokenCount: number;
  loadError?: string;
  reload: () => Promise<void> | void;
}

/** The conversation with Carmen, laid out like a messaging app. */
const ClientCorrespondence = ({ token, quoteNumber, portal, studioWaiting, spokenCount, loadError, reload }: ChatProps) => {
  const [text, setText] = useState('');
  const [replyEmail, setReplyEmail] = useState('');
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const whatsApp = clientWhatsAppUrl(quoteNumber, Boolean(portal?.booking?.dateConfirmed));

  const send = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!text.trim()) {
      setError('Write a short message first.');
      return;
    }
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const response = await fetch(`${API_BASE_URL}/public/quotes/${token}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: text.trim(),
          clientRequestId: crypto.randomUUID(),
          contact: replyEmail.trim() ? { email: replyEmail.trim() } : undefined,
        }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || 'The message could not be sent.');
      setText('');
      setReplyEmail('');
      setNotice('Sent. Carmen will reply here.');
      await reload();
    } catch (sendError) {
      setError(sendError instanceof Error ? sendError.message : 'The message could not be sent.');
    } finally {
      setBusy(false);
    }
  };

  const needsReplyAddress = Boolean(portal && !portal.contact?.hasEmail && !portal.contact?.hasPhone);
  const subtitle = studioWaiting ? 'New message from Carmen' : spokenCount > 0 ? 'Your conversation' : 'Questions about your shoot? Write here.';

  return (
    <section
      id="client-chat"
      className={`client-chat${studioWaiting ? ' has-new' : ''}${spokenCount === 0 ? ' is-empty' : ''}`}
      aria-labelledby="client-chat-title"
    >
      <header className="client-chat-head">
        <img src="/brand/carmen-lorraine-mark.svg" alt="" />
        <div>
          <h2 id="client-chat-title">Carmen</h2>
          <p>{subtitle}</p>
        </div>
      </header>
      {spokenCount > 0 ? (
        <CorrespondenceTimeline entries={portal?.timeline || []} conversationOnly chat perspective="client" />
      ) : null}
      {needsReplyAddress ? (
        <label className="client-chat-address">
          A reply address, if you would like one
          <input value={replyEmail} onChange={(event) => setReplyEmail(event.target.value)} inputMode="email" autoComplete="email" />
        </label>
      ) : null}
      <form className="chat-composer" onSubmit={send}>
        <textarea
          id="client-message"
          aria-label="Write a message to Carmen"
          rows={1}
          value={text}
          maxLength={2000}
          onChange={(event) => setText(event.target.value)}
          placeholder="Write a message"
        />
        <button type="submit" className="chat-send" disabled={busy || !text.trim()}>Send</button>
      </form>
      {notice ? <p className="chat-hint" role="status">{notice}</p> : null}
      {error || loadError ? <p className="chat-hint chat-error" role="alert">{error || loadError}</p> : null}
      {whatsApp ? <p className="chat-hint"><a href={whatsApp} target="_blank" rel="noreferrer">Prefer WhatsApp? Chat there instead</a></p> : null}
    </section>
  );
};

export default ClientCorrespondence;
